import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import * as authHelper from "../api/loginAction";
import { getUserData, login } from "../api/loginapi";
import SplashScreen from "./SplashScreen";

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => { },
  currentUser: undefined,
  setCurrentUser: () => { },
  logout: () => { },
  promoShow: false,
  setPromoShow: () => { },
};

const AuthContext = createContext(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const fontCss = {
  color: "#181a20",
  fontWeight: "bold",
};

const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState();
  const [promoShow, setPromoShow] = useState(false);

  const saveAuth = (auth) => {
    setAuth(auth);
    if (auth) {
      authHelper.setAuth(auth);
    } else {
      authHelper.removeAuth();
    }
  };

  const logout = () => {
    localStorage.removeItem("UserData");
    sessionStorage.clear();
    saveAuth(undefined);
    setCurrentUser(undefined);
    // window.location.reload();
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        promoShow,
        setPromoShow,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit = ({ children }) => {
  const { auth, logout, setCurrentUser, saveAuth } = useAuth();
  const didRequest = useRef(false);
  const intl = useIntl();
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [splashScreenError, setSplashScreenError] = useState(null);
  const isMM6MM = process.env.REACT_APP_PROJECT_ID === "mm6mm";

  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          const data = getUserData();

          if (data) {
            setCurrentUser(data);
          }
        }
      } catch (error) {
        console.error(error);
        if (!didRequest.current) {
          logout();
        }
      } finally {
        setShowSplashScreen(false);
      }

      return () => (didRequest.current = true);
    };

    const handleLanguageChange = (language) => {
      if (sessionStorage.getItem("platform") === "mobile") {
        localStorage.setItem("locale", language);
      } else {
        localStorage.setItem("locale", language);
        window.location.reload();
      }
    };

    const handleAutoLogin = async (telegramid, accesscode) => {
      try {
        const queryParams = {
          action: "mbmm6mmlogin",
          telegramid: telegramid,
          accesscode: accesscode,
          ipaddress: "",
          languagecode: intl.locale,
          projectid: process.env.REACT_APP_PROJECT_ID,
        };

        const resp = await login(queryParams);

        if (parseInt(resp.code) === 0) {
          const params = new URLSearchParams(window.location.search);
          saveAuth({ ...resp, fullName: decodeURIComponent(params.get("fullname")), profilePic: decodeURIComponent(params.get("profileimage")) });
          setCurrentUser({ ...resp, fullName: decodeURIComponent(params.get("fullname")), profilePic: decodeURIComponent(params.get("profileimage")) });
          window.history.replaceState({}, document.title, window.location.pathname);
          handleLanguageChange(resp.languagecode);
          setShowSplashScreen(false);
        } else {
          console.error("Auto login failed", resp.message);
          logout();
          setShowSplashScreen(true);
          setSplashScreenError(resp.message);
        }
      } catch (error) {
        console.error("Auto login error", error);
        logout();
        setShowSplashScreen(true);
        setSplashScreenError("Login Failed");
      } finally {
        // showSplashScreen is handled in success/error blocks.
      }
    };

    if (auth && !isMM6MM) {
      requestUser();
    } else if (isMM6MM) {
      const params = new URLSearchParams(window.location.search);
      const telegramid = params.get("telegramid");
      const accesscode = params.get("accesscode");
      const platformParam = params.get("platform");
      if (platformParam) {
        localStorage.setItem("platform", platformParam);
      }

      if (telegramid && accesscode) {
        handleAutoLogin(telegramid, accesscode);
      } else {
        setShowSplashScreen(!auth);
        setSplashScreenError("Session End");
      }
    } else {
      logout();
      setShowSplashScreen(false);
    }
    // eslint-disable-next-line
  }, []);

  return showSplashScreen ? <SplashScreen error={splashScreenError} /> : <>{children}</>;
};

export { AuthInit, AuthProvider, fontCss, useAuth };

